import { useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Stack, Alert, FormControlLabel, Checkbox, Box, Divider, Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { vendorNav } from './VendorBids';
import api from '../services/api';

export default function SubmitBid() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  const [technical, setTechnical] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [declared, setDeclared] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [alreadyBid, setAlreadyBid] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/tenders/${tenderId}`);
        setTender(data.data);
      } catch {
        setError('Tender not found or no longer open.');
      }
    })();
  }, [tenderId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/bids/mine');
        const existing = data.data.find((b) => b.tenderId === tenderId && b.status !== 'WITHDRAWN');
        if (existing) setAlreadyBid(existing);
      } catch { /* ignore */ }
    })();
  }, [tenderId]);

  const submit = async () => {
    setError('');
    if (!technical || !financial) { setError('Both technical and financial proposals are required.'); return; }
    if (!declared) { setError('Please confirm the declaration before submitting.'); return; }

    const fd = new FormData();
    fd.append('TECHNICAL', technical);
    fd.append('FINANCIAL', financial);

    setBusy(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/bids`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      navigate('/vendor/bids/confirmation', { state: { reference: data.data.reference, tenderTitle: tender?.title, resubmitted: data.data.resubmitted } });
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not submit your bid. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const deadline = tender?.submissionDeadline ? new Date(tender.submissionDeadline) : null;
  // TEMP (historical data entry): deadline block disabled — restore the line below to re-enable
  const past = deadline && new Date() > deadline;

  return (
    <DashboardLayout nav={vendorNav} title="Submit Bid">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {alreadyBid && <Alert severity="warning" sx={{ mb: 2 }}>You have already submitted a bid ({alreadyBid.reference}) for this tender. Submitting again will replace your previous bid.</Alert>}

      {tender && (
        <Paper sx={{ p: 3, maxWidth: 720 }}>
          <Typography variant="h6" gutterBottom>{tender.title}</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip size="small" label={tender.reference} color="primary" />
            <Chip size="small" label={tender.category} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Submission deadline: {deadline ? deadline.toLocaleString('en-GB') : 'â€”'}
          </Typography>

          {past && <Alert severity="warning" sx={{ mt: 2 }}>The submission deadline has passed. Bids can no longer be submitted.</Alert>}

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>Technical Proposal</Typography>
          <Button component="label" variant={technical ? 'contained' : 'outlined'} color={technical ? 'success' : 'primary'} disabled={past}>
            {technical ? `âœ“ ${technical.name}` : 'Upload technical proposal'}
            <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setTechnical(e.target.files[0])} />
          </Button>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>Financial Proposal</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Encrypted at rest and kept sealed until the evaluation stage.
          </Typography>
          <Button component="label" variant={financial ? 'contained' : 'outlined'} color={financial ? 'success' : 'primary'} disabled={past}>
            {financial ? `âœ“ ${financial.name}` : 'Upload financial proposal'}
            <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setFinancial(e.target.files[0])} />
          </Button>

          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={declared} onChange={(e) => setDeclared(e.target.checked)} disabled={past} />}
              label="I declare that the information and documents provided are accurate and complete."
            />
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" size="large" onClick={submit} disabled={busy || past}>
              {busy ? 'Submittingâ€¦' : 'Submit Bid'}
            </Button>
            <Button variant="text" onClick={() => navigate('/vendor/tenders')}>Cancel</Button>
          </Stack>
        </Paper>
      )}
    </DashboardLayout>
  );
}

